import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';

const About = () => {    
    return (
        <div className="container mx-auto px-4 md:px-6 pt-24 pb-16">
            {/* SEO Settings */}
            <title>{`About ${process.env.REACT_APP_NAME} – Our Mission to Connect Talent with Opportunity`}</title>
            <meta
                name="description"
                content="Learn about Hunar Pool and our mission to help job seekers discover meaningful career opportunities while helping employers find skilled professionals."
            />

            {/* Hero Section */}
            <div className="text-center mb-16">
                <h1 className="text-4xl md:text-5xl font-bold mb-6">About <span className="gradient-text">Hunar Pool</span></h1>
                <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
                    We're revolutionizing job searching with AI-powered matching that connects
                    talented professionals with their ideal career opportunities.
                </p>
            </div>
            
            {/* Our Mission */}
            <div className="section-bg rounded-2xl p-8 md:p-12 mb-12 border border-gray-800">
                <div className="grid md:grid-cols-2 gap-8 items-center">
                <div>
                    <h2 className="text-3xl font-bold mb-6">Our Mission</h2>
                    <p className="text-gray-300 mb-6">
                        At Hunar Pool, we believe that finding the right job should be
                        intuitive, efficient, and rewarding. Traditional job searching is
                        often frustrating, time-consuming, and disconnected from what truly
                        matters: matching skills, values, and career aspirations.
                    </p>
                    <p className="text-gray-300">
                        Our mission is to transform this experience using artificial
                        intelligence to create meaningful connections between talented
                        professionals and companies that value their skills.
                    </p>
                </div>
                <div className="flex justify-center">
                    <div className="w-64 h-64 rounded-full bg-gradient-to-br from-[#0096C7]/20 to-[#4361EE]/20 flex items-center justify-center">
                    <i className="fas fa-bullseye text-6xl text-[#0096C7]" />
                    </div>
                </div>
                </div>
            </div>
            {/* What We Do */}
            <div className="mb-16">
                <h2 className="text-3xl font-bold text-center mb-12">What We Do</h2>
                <div className="grid md:grid-cols-3 gap-8">
                <div className="bg-gray-900 rounded-2xl p-8 border border-gray-800">
                    <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-[#0096C7]/20 to-[#4361EE]/20 flex items-center justify-center mb-6">
                    <i className="fas fa-robot text-2xl text-[#0096C7]" />
                    </div>
                    <h3 className="text-xl font-bold mb-4">AI-Powered Matching</h3>
                    <p className="text-gray-300">
                        Our advanced algorithms analyze skills, experience, and preferences to
                        match candidates with opportunities that truly fit their career goals.
                    </p>
                </div>
                <div className="bg-gray-900 rounded-2xl p-8 border border-gray-800">
                    <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-[#06D6A0]/20 to-[#0096C7]/20 flex items-center justify-center mb-6">
                    <i className="fas fa-search text-2xl text-[#06D6A0]" />
                    </div>
                    <h3 className="text-xl font-bold mb-4">Smart Job Discovery</h3>
                    <p className="text-gray-300">
                        Discover opportunities across multiple platforms through our unified
                        search interface, saving you time and effort in your job search.
                    </p>
                </div>
                <div className="bg-gray-900 rounded-2xl p-8 border border-gray-800">
                    <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-[#FFD166]/20 to-[#06D6A0]/20 flex items-center justify-center mb-6">
                    <i className="fas fa-chart-line text-2xl text-[#FFD166]" />
                    </div>
                    <h3 className="text-xl font-bold mb-4">Career Insights</h3>
                    <p className="text-gray-300">
                        Get personalized career advice, skill gap analysis, and industry
                        trends to help you grow professionally and make informed decisions.
                    </p>
                </div>
                </div>
            </div>
            {/* Our Story */}
            <div className="section-bg rounded-2xl p-8 md:p-12 mb-12 border border-gray-800">
                <h2 className="text-3xl font-bold mb-8">Our Story</h2>
                <div className="space-y-6 text-gray-300">
                <p>
                    Founded in 2023, Hunar Pool began as a simple idea: what if job
                    searching could be as personalized and efficient as the best
                    recommendation systems? Our founders, coming from both tech and
                    recruitment backgrounds, saw firsthand how traditional methods were
                    failing both job seekers and employers.
                </p>
                <p>
                    We started with a small team of AI researchers, data scientists, and HR
                    experts working to build a platform that truly understands what makes a
                    job match successful. Today, we've helped thousands of professionals
                    find meaningful work and continue to innovate in the recruitment
                    technology space.
                </p>
                <p>
                    The name "Hunar Pool" comes from the Hindi word "Hunar" meaning skill or
                    talent, representing our commitment to helping talented individuals find
                    opportunities where their skills are valued and appreciated.
                </p>
                </div>
            </div>
            {/* Our Values */}
            <div className="mb-16">
                <h2 className="text-3xl font-bold text-center mb-12">Our Values</h2>
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-gray-900 rounded-xl p-6 border border-gray-800 text-center">
                    <div className="w-12 h-12 rounded-lg bg-[#0096C7]/20 flex items-center justify-center mx-auto mb-4">
                    <i className="fas fa-users text-[#0096C7]" />
                    </div>
                    <h4 className="font-bold mb-2">User-First</h4>
                    <p className="text-sm text-gray-300">Everything we build prioritizes the needs of job seekers.</p>
                </div>
                <div className="bg-gray-900 rounded-xl p-6 border border-gray-800 text-center">
                    <div className="w-12 h-12 rounded-lg bg-[#06D6A0]/20 flex items-center justify-center mx-auto mb-4">
                    <i className="fas fa-shield-alt text-[#06D6A0]" />
                    </div>
                    <h4 className="font-bold mb-2">Transparency</h4>
                    <p className="text-sm text-gray-300">Clear, honest communication in all our processes.</p>
                </div>
                <div className="bg-gray-900 rounded-xl p-6 border border-gray-800 text-center">
                    <div className="w-12 h-12 rounded-lg bg-[#FFD166]/20 flex items-center justify-center mx-auto mb-4">
                    <i className="fas fa-lightbulb text-[#FFD166]" />
                    </div>
                    <h4 className="font-bold mb-2">Innovation</h4>
                    <p className="text-sm text-gray-300">Continuously improving through technology and insights.</p>
                </div>
                <div className="bg-gray-900 rounded-xl p-6 border border-gray-800 text-center">
                    <div className="w-12 h-12 rounded-lg bg-[#4361EE]/20 flex items-center justify-center mx-auto mb-4">
                    <i className="fas fa-heart text-[#4361EE]" />
                    </div>
                    <h4 className="font-bold mb-2">Empowerment</h4>
                    <p className="text-sm text-gray-300">
                        Helping individuals take control of their careers.
                    </p>
                </div>
                </div>
            </div>
            {/* CTA Section */}
            <div className="section-bg rounded-2xl p-8 md:p-12 text-center border border-gray-800">
                <h2 className="text-3xl font-bold mb-6">
                    Ready to Find Your Perfect Match?
                </h2>
                <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
                    Join thousands of professionals who have transformed their job search with Hunar Pool.
                </p>
                <a
                href="job-listing.html"
                className="inline-block bg-gradient-to-r from-[#0096C7] to-[#4361EE] hover:from-[#00a8e0] hover:to-[#4a6bf5] text-white font-semibold px-8 py-4 rounded-xl transition-all duration-300 hover:shadow-lg hover:shadow-[#0096C7]/20"
                >Start Your Job Search</a>
            </div>
        </div>
    );
};



export default About;